import { TranslationKey } from './../../../types/translation/TranslationKey';
import { ProductMasterData } from '../../../types/product_master_data/productMasterData';
import { t } from '../../../types/translation/Translator';
import { toMap } from '../../../util/map.util';
import { TableHeader } from '../../components/Table';
import { Lot } from '../../../types/lot/lot';
import dayjs from 'dayjs';
import { Company } from '../../../types/company';
import { LotTableSettingsColumn } from '../../../types/table_settings/lot/LotTableSettings';
import { CustomFieldType, CustomFieldUtil } from '../../../types/custom_field';
import { TableSettings } from '../../../types/table_settings/TableSettings';
import { toFilterString } from '../../../util/string.util';

export namespace LotTable {
  export const header = (
    productMasterData: Map<string, ProductMasterData>,
    companies: Map<string, Company>,
    settings: TableSettings<any>,
    company: Company,
    locale: string,
  ) => {
    const wantedColumns = toMap(settings.columns, 'column');

    const columns: TableHeader<Lot>[] = [];

    const isPartner = company.partner;

    if (isPartner) {
      columns.push({
        key: 'company',
        name: t(TranslationKey.company).singular.label,
        text: (item: Lot) => companies.get(item.companyId)?.name || '',
      });
    }

    if (wantedColumns.has(LotTableSettingsColumn.productName)) {
      columns.push({
        key: 'name',
        name: wantedColumns.get(LotTableSettingsColumn.productName)?.label || '',
        text: (item: Lot) =>
          productMasterData.get(item.productMasterDataId)?.productName ||
          t(TranslationKey.unknownProductMasterData).singular.label,
        column: LotTableSettingsColumn.productName,
      });
    }

    if (wantedColumns.has(LotTableSettingsColumn.productNumber)) {
      columns.push({
        key: 'productNumber',
        name: wantedColumns.get(LotTableSettingsColumn.productNumber)?.label || '',
        text: (item: Lot) =>
          productMasterData.get(item.productMasterDataId)?.productNumber ||
          t(TranslationKey.unknownProductMasterData).singular.label,
        column: LotTableSettingsColumn.productNumber,
      });
    }

    if (wantedColumns.has(LotTableSettingsColumn.lotExpiry)) {
      columns.push({
        key: 'expiry',
        name: wantedColumns.get(LotTableSettingsColumn.lotExpiry)?.label || '',
        text: (item: Lot) => {
          if (!item.expirationDate) return '';

          const expiration = dayjs(item.expirationDate);
          return expiration.isValid() ? expiration.toDate().toLocaleDateString(locale) : '';
        },
        sortValue: (item: Lot) => (item.expirationDate ? new Date(item.expirationDate) : undefined),
        column: LotTableSettingsColumn.lotExpiry,
      });
    }

    if (wantedColumns.has(LotTableSettingsColumn.lotNumber)) {
      columns.push({
        key: 'number',
        name: wantedColumns.get(LotTableSettingsColumn.lotNumber)?.label || '',
        text: (item: Lot) => item.number,
        column: LotTableSettingsColumn.lotNumber,
      });
    }

    const defaultKeys = new Set(Object.keys(LotTableSettingsColumn));
    const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

    customFieldColumns.forEach(item => {
      columns.push({
        key: item.column,
        name: item.label,
        sortValue: (lot: Lot) => {
          const customFieldValue = lot.customFieldValues().find(i => i.name === item.column);
          if (!customFieldValue) return '';
          if (customFieldValue.type === CustomFieldType.date) return dayjs(customFieldValue.value);
          return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
        },
        text: (lot: Lot) => {
          const customFieldValue = lot.customFieldValues().find(i => i.name === item.column);
          if (!customFieldValue) return '';
          return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
        },
        column: item.column,
      });
    });

    return columns.sort(
      (a: TableHeader<Lot>, b: TableHeader<Lot>) =>
        (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
    );
  };
}

export const lotTableFilter = (
  item: Lot,
  filter: string,
  productMasterData: Map<string, ProductMasterData>,
  settings?: TableSettings<any>,
) => {
  const filterString = toFilterString(filter);
  const wantedColumns = toMap(settings?.columns || [], 'column');
  const hasConfiguredColumns = wantedColumns.size > 0;

  if (!filter) return true;

  const shouldCheck = (column: string) => !hasConfiguredColumns || wantedColumns.has(column);

  if (shouldCheck(LotTableSettingsColumn.lotNumber) && toFilterString(item.number).includes(filterString)) {
    return true;
  }

  if (
    shouldCheck(LotTableSettingsColumn.productName) &&
    toFilterString(productMasterData.get(item.productMasterDataId)?.productName).includes(filterString)
  ) {
    return true;
  }

  if (
    shouldCheck(LotTableSettingsColumn.productNumber) &&
    toFilterString(productMasterData.get(item.productMasterDataId)?.productNumber).includes(filterString)
  ) {
    return true;
  }

  if (
    shouldCheck(LotTableSettingsColumn.lotExpiry) &&
    toFilterString(item.expirationDate ? dayjs(item.expirationDate).toDate().toLocaleDateString() : '').includes(
      filterString,
    )
  ) {
    return true;
  }

  if (hasConfiguredColumns) {
    const defaultKeys = new Set(Object.keys(LotTableSettingsColumn));
    const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

    for (const custom of customFieldColumns) {
      const field = toFilterString(item.customFieldValues().find(i => i.name === custom.column)?.value);
      if (field.includes(filterString)) return true;
    }
  }

  return false;
};
